/**
 * <ImageLightbox> - Iter 9.40 Pass I
 *
 * Universal click-to-expand image viewer. Every image anywhere on the platform
 * can trigger this. Supports prev/next when given an array, or single image.
 *
 * Usage:
 *   const [lightbox, setLightbox] = useState(null);
 *   <img onClick={() => setLightbox({ images: [url1, url2], index: 0 })} />
 *   <ImageLightbox value={lightbox} onClose={() => setLightbox(null)} />
 */
import { useEffect, useCallback } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { createPortal } from "react-dom";

export default function ImageLightbox({ value, onClose }) {
  const images = value?.images || (value?.src ? [value.src] : []);
  const index = value?.index || 0;

  const goPrev = useCallback((e) => {
    e?.stopPropagation();
    if (!value) return;
    const next = (index - 1 + images.length) % images.length;
    value.__onChange?.(next);
  }, [value, index, images.length]);

  const goNext = useCallback((e) => {
    e?.stopPropagation();
    if (!value) return;
    const next = (index + 1) % images.length;
    value.__onChange?.(next);
  }, [value, index, images.length]);

  useEffect(() => {
    if (!value) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [value, onClose, goPrev, goNext]);

  if (!value || images.length === 0) return null;
  const src = images[index] || images[0];

  return createPortal(
    <div
      className="fixed inset-0 z-[120] bg-black/90 flex items-center justify-center p-4 cursor-zoom-out"
      onClick={onClose}
      data-testid="image-lightbox"
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
        data-testid="lightbox-close"
      >
        <X className="w-5 h-5" />
      </button>

      {images.length > 1 && (
        <>
          <button
            onClick={goPrev}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
            data-testid="lightbox-prev"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={goNext}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
            data-testid="lightbox-next"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
          <div className="absolute bottom-5 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-white/10 text-white text-xs font-mono" data-testid="lightbox-counter">
            {index + 1} / {images.length}
          </div>
        </>
      )}

      <img
        src={src}
        alt="Full size"
        className="max-w-full max-h-[92vh] object-contain rounded-lg shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="lightbox-image"
      />
    </div>,
    document.body
  );
}
